'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowRight, CheckCircle2, Loader2, RefreshCw, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { trackMarketingEvent } from '@/components/analytics/MarketingAnalytics';
import { apiClient } from '@/lib/api-client';
import { useLandingLanguage } from '@/hooks/useLandingLanguage';
import { Card } from './shared';
import { cn } from '@/lib/utils';
const getValidationMessages = (isEn: boolean) => ({
  required: isEn ? 'This field is required.' : 'Ovo polje je obavezno.',
  invalidEmail: isEn ? 'Enter a valid email address.' : 'Unesite ispravnu email adresu.',
  tooLong: isEn ? 'Input is too long.' : 'Unos je predugačak.'
});
const createSchema = (isEn: boolean) => {
  const msg = getValidationMessages(isEn);
  return z.object({
    firstName: z.string().min(1, msg.required).max(100, msg.tooLong),
    lastName: z.string().min(1, msg.required).max(100, msg.tooLong),
    email: z.string().min(1, msg.required).email(msg.invalidEmail).max(200, msg.tooLong),
    company: z.string().min(1, msg.required).max(200, msg.tooLong),
    phone: z.string().max(50, msg.tooLong).optional(),
    employeeRange: z.string().max(20, msg.tooLong).optional(),
    locationCount: z.string().min(1, msg.required).max(20, msg.tooLong),
    terminalCount: z.string().min(1, msg.required).max(20, msg.tooLong),
    currentSolution: z.string().min(1, msg.required).max(50, msg.tooLong),
    message: z.string().max(4000, msg.tooLong).optional(),
    website: z.string().max(0).optional()
  });
};
type FormValues = z.infer<ReturnType<typeof createSchema>>;
const SUCCESS_VISIBLE_MS = 8000;
type SelectOption = {
  value: string;
  label: string;
};
function FormSelect(t0) {
  const $ = _c(16);
  const {
    control,
    name,
    label,
    placeholder,
    options,
    error
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("h-12 w-full rounded-xl border border-white/[0.12] bg-cadrino-bg px-4 text-sm text-slate-100", "ring-offset-0 focus:ring-2 focus:ring-cadrino-primary/40 focus:ring-offset-0", "data-[placeholder]:text-slate-500");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const inputClass = t1;
  let t2;
  if ($[1] !== label) {
    t2 = <label className="mb-1 block text-xs font-medium text-cadrino-muted">{label}</label>;
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== options || $[4] !== placeholder) {
    t3 = t4 => {
      const {
        field
      } = t4;
      return <Select value={field.value || undefined} onValueChange={field.onChange}><SelectTrigger className={inputClass}><SelectValue placeholder={placeholder} /></SelectTrigger><SelectContent className={cn("rounded-xl border border-slate-200 bg-white text-slate-800 shadow-xl", "dark:border-white/[0.12] dark:bg-[#0D1628] dark:text-slate-100", "backdrop-blur-xl")}>{options.map(_temp)}</SelectContent></Select>;
    };
    $[3] = options;
    $[4] = placeholder;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== control || $[7] !== name || $[8] !== t3) {
    t4 = <Controller control={control} name={name} render={t3} />;
    $[6] = control;
    $[7] = name;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== error) {
    t5 = error && <p className="mt-1 text-xs text-red-400">{error}</p>;
    $[10] = error;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t2 || $[13] !== t4 || $[14] !== t5) {
    t6 = <div>{t2}{t4}{t5}</div>;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
function _temp(o) {
  return <SelectItem key={o.value} value={o.value} className={cn("rounded-lg py-2.5 pl-8 pr-3 text-sm text-slate-700", "focus:bg-slate-100 focus:text-slate-900", "dark:text-slate-200 dark:focus:bg-white/[0.08] dark:focus:text-white")}>{o.label}</SelectItem>;
}
export default function CadrinoContactForm() {
  const {
    t
  } = useTranslation('landing');
  const {
    language
  } = useLandingLanguage();
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const isEn = language === 'en';
  const reducedMotion = useReducedMotion();
  const schema = useMemo(() => createSchema(isEn), [isEn]);
  const employeeOptions = t('contactSection.employees.options', {
    returnObjects: true
  }) as SelectOption[];
  const locationOptions = t('contactSection.locations.options', {
    returnObjects: true
  }) as SelectOption[];
  const terminalOptions = t('contactSection.terminals.options', {
    returnObjects: true
  }) as SelectOption[];
  const solutionOptions = t('contactSection.currentSolution.options', {
    returnObjects: true
  }) as SelectOption[];
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: {
      errors,
      isSubmitting
    }
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      website: '',
      employeeRange: '',
      locationCount: '',
      terminalCount: '',
      currentSolution: '',
      message: ''
    }
  });
  const inputClass = cn('w-full rounded-xl border border-white/[0.12] bg-cadrino-bg px-4 py-3 text-sm text-slate-100', 'placeholder:text-slate-500 focus:border-cadrino-primary focus:outline-none focus:ring-2 focus:ring-blue-500/20');
  const onSubmit = async (data: FormValues) => {
    if (data.website) return;
    setStatus('idle');
    setErrorMsg(null);
    try {
      await apiClient.submitSalesLead({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        company: data.company,
        phone: data.phone || undefined,
        employeeRange: data.employeeRange || undefined,
        locationCount: data.locationCount,
        terminalCount: data.terminalCount,
        currentSolution: data.currentSolution,
        message: data.message?.trim() || undefined,
        preferredLanguage: language
      });
      setStatus('success');
      trackMarketingEvent('lead_submit_success');
      reset({
        website: '',
        employeeRange: '',
        locationCount: '',
        terminalCount: '',
        currentSolution: '',
        message: ''
      });
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : t('contactSection.error'));
    }
  };
  useEffect(() => {
    if (status !== 'success') return;
    const timer = window.setTimeout(() => {
      setStatus('idle');
      setErrorMsg(null);
    }, SUCCESS_VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [status]);
  return <Card className="p-6 md:p-8">
      <h3 className="text-lg font-semibold text-slate-100">
        {t('contactSection.formTitle')}
      </h3>

      <AnimatePresence mode="wait" initial={false}>
        {status === 'success' ? <motion.div key="success-state" initial={reducedMotion ? undefined : {
        opacity: 0,
        y: 10,
        scale: 0.995
      }} animate={reducedMotion ? undefined : {
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reducedMotion ? undefined : {
        opacity: 0,
        y: -8,
        scale: 0.995
      }} transition={reducedMotion ? undefined : {
        duration: 0.28,
        ease: 'easeOut'
      }} className="mt-6">
            <div className="rounded-2xl border border-emerald-500/35 bg-emerald-50/95 p-4 text-emerald-950 shadow-[0_8px_24px_-12px_rgba(16,185,129,0.45)] sm:p-5 dark:border-emerald-400/30 dark:bg-emerald-950/35 dark:text-emerald-100">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-200 text-emerald-700 dark:bg-emerald-400/20 dark:text-emerald-200">
                  <CheckCircle2 className="h-4.5 w-4.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">
                    {isEn ? 'Request sent successfully' : 'Upit je uspešno poslat'}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-emerald-800 dark:text-emerald-100/90">{t('contactSection.success')}</p>
                  <p className="mt-2 text-sm leading-relaxed text-emerald-700 dark:text-emerald-100/80">{t('contactSection.successDetail')}</p>
                  <Link href="/product" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800 transition-colors hover:text-emerald-900 dark:text-emerald-200 dark:hover:text-emerald-100">
                    {isEn ? 'Open product overview' : 'Pogledajte proizvod'}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div> : <motion.div key="form-state" initial={reducedMotion ? undefined : {
        opacity: 0,
        y: 10,
        scale: 0.995
      }} animate={reducedMotion ? undefined : {
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reducedMotion ? undefined : {
        opacity: 0,
        y: -8,
        scale: 0.995
      }} transition={reducedMotion ? undefined : {
        duration: 0.28,
        ease: 'easeOut'
      }}>
            {status === 'error' && <div className="mt-6 rounded-2xl border border-rose-500/40 bg-rose-50/95 p-4 text-rose-950 shadow-[0_8px_24px_-12px_rgba(244,63,94,0.45)] sm:p-5 dark:border-rose-400/35 dark:bg-rose-950/35 dark:text-rose-100">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rose-200 text-rose-700 dark:bg-rose-400/20 dark:text-rose-200">
                    <TriangleAlert className="h-4.5 w-4.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-rose-900 dark:text-rose-100">
                      {isEn ? 'Submission failed' : 'Slanje nije uspelo'}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-rose-800 dark:text-rose-100/90">{errorMsg ?? t('contactSection.error')}</p>
                    <button type="button" onClick={() => {
                setStatus('idle');
                setErrorMsg(null);
              }} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-rose-800 transition-colors hover:text-rose-900 dark:text-rose-200 dark:hover:text-rose-100">
                      <RefreshCw className="h-3.5 w-3.5" />
                      {isEn ? 'Try again' : 'Pokušajte ponovo'}
                    </button>
                  </div>
                </div>
              </div>}
            <form className="mt-6 space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
          {isSubmitting && <div className="flex items-center gap-3 rounded-xl border border-cadrino-primary/35 bg-cadrino-primary/10 px-4 py-3 text-sm text-blue-100">
              <Loader2 className="h-4 w-4 animate-spin text-cadrino-primary" />
              <div className="leading-relaxed">
                <p className="font-semibold text-slate-100">
                  {isEn ? 'Sending your request...' : 'Šaljemo vaš upit...'}
                </p>
                <p className="text-xs text-cadrino-muted">
                  {isEn ? 'Please wait a moment, do not close this page.' : 'Sačekajte trenutak, nemojte zatvarati ovu stranicu.'}
                </p>
              </div>
            </div>}
          <input type="text" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden {...register('website')} />
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-cadrino-muted">
                {t('contactSection.labels.firstName')}
              </label>
              <input className={inputClass} {...register('firstName')} />
              {errors.firstName && <p className="mt-1 text-xs text-red-400">{errors.firstName.message}</p>}
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-cadrino-muted">
                {t('contactSection.labels.lastName')}
              </label>
              <input className={inputClass} {...register('lastName')} />
              {errors.lastName && <p className="mt-1 text-xs text-red-400">{errors.lastName.message}</p>}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-cadrino-muted">
              {t('contactSection.labels.email')}
            </label>
            <input type="email" className={inputClass} {...register('email')} />
            {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email.message}</p>}
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-cadrino-muted">
              {t('contactSection.labels.company')}
            </label>
            <input className={inputClass} {...register('company')} />
            {errors.company && <p className="mt-1 text-xs text-red-400">{errors.company.message}</p>}
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-cadrino-muted">
              {t('contactSection.labels.phone')}
            </label>
            <input type="tel" className={inputClass} {...register('phone')} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormSelect control={control} name="employeeRange" label={t('contactSection.labels.employees')} placeholder={t('contactSection.employees.placeholderOption')} options={employeeOptions} />
            <FormSelect control={control} name="locationCount" label={t('contactSection.labels.locations')} placeholder={t('contactSection.locations.placeholderOption')} options={locationOptions} error={errors.locationCount?.message} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormSelect control={control} name="terminalCount" label={t('contactSection.labels.terminals')} placeholder={t('contactSection.terminals.placeholderOption')} options={terminalOptions} error={errors.terminalCount?.message} />
            <FormSelect control={control} name="currentSolution" label={t('contactSection.labels.currentSolution')} placeholder={t('contactSection.currentSolution.placeholderOption')} options={solutionOptions} error={errors.currentSolution?.message} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-cadrino-muted">
              {t('contactSection.labels.message')}
            </label>
            <textarea rows={3} className={inputClass} {...register('message')} />
          </div>
          <Button type="submit" className="h-11 w-full rounded-xl bg-cadrino-primary hover:bg-blue-600" disabled={isSubmitting}>
            {isSubmitting ? <>
                <Loader2 className="animate-spin" />
                {t('contactSection.submitting')}
              </> : t('contactSection.submit')}
          </Button>
          <p className="text-xs text-cadrino-muted">
            {t('contactSection.privacyNote')}{' '}
            <Link href="/privacy" className="underline hover:text-slate-200">
              {t('contactSection.privacyLink')}
            </Link>
            .
          </p>
            </form>
          </motion.div>}
      </AnimatePresence>
    </Card>;
}